import { useEffect, useState } from "react";
import { LuLoaderCircle } from "react-icons/lu";
import { Tooltip } from "react-tooltip";
import { StripeApi } from "../../api/stripeApi";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { useSubscriptionActions } from "../../core_stores/useSubscriptionStore";
import { colors, typography, spacing, effects, layout, components } from "../../design/tokens";
import { SubscriptionProduct } from "../../model/subscriptionProduct";
import { useAuthModal } from "../../providers/AuthModalProvid";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { useSubscriptionsModal } from "../../providers/SubscriptionsModalProvider";
import { trackEvent } from "../../utils/analyticsUtils";
import { usePromptInputActions } from "../../view_stores/usePromptInputStore";
import SubscriptionsModalItem from "./components/SubscriptionsModalItem";
import SubscriptionsModalLayout from "./components/SubscriptionsModalLayout";
import SubscriptionsRemainingTokens from "./components/SubscriptionsRemainingTokens";

interface ProductsConfig {
  firstPlan: SubscriptionProduct;
  secondPlan: SubscriptionProduct;
  scaleOptions: SubscriptionProduct[];
}

function transformProducts(products: SubscriptionProduct[]): ProductsConfig {
  // Backend returns paid plans in order: Build ($20), Grow ($50), Scale options
  return {
    firstPlan: products[0],
    secondPlan: products[1],
    scaleOptions: products.slice(2),
  };
}

export default function SubscriptionsModal({
  onClose,
}: {
  onClose?: () => void;
}) {
  const isUserSignedIn = useIsUserSignedIn();

  const { reloadSubscriptionsInfo } = useSubscriptionActions();

  useEffect(() => {
    if (isUserSignedIn) {
      reloadSubscriptionsInfo();
    }
  }, [isUserSignedIn]);

  // Prevent background scroll when modal is open and compensate for scrollbar
  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    document.body.style.height = "100vh";
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.body.style.paddingRight = "";
    };
  }, []);

  // Free tier is no longer returned by backend - never show free tier card
  const showFreeTier = false;

  return (
    <>
      <SubscriptionsModalLayout onClose={onClose} showFreeTier={showFreeTier}>
        <Content />
      </SubscriptionsModalLayout>
      <Tooltip
        id="remaining-tokens-tooltip"
        place="top"
        className="max-w-[95%]"
        opacity={1.0}
        openOnClick={true}
      >
        Unused tokens will not accumulate or roll over from month to month. Each
        billing cycle resets your token balance.
      </Tooltip>
    </>
  );
}

function Content() {
  const isUserSignedIn = useIsUserSignedIn();
  const isCompactView = useIsCompactView();

  const { show: showAuthModal } = useAuthModal();
  const { close: closeSubscriptionsModal } = useSubscriptionsModal();
  const { saveInLocalStorage: savePromptInLocalStorage } =
    usePromptInputActions();

  const [products, setProducts] = useState<SubscriptionProduct[] | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [isCheckoutSessionLoading, setIsCheckoutSessionLoading] =
    useState(false);
  const [selectedScaleIndex, setSelectedScaleIndex] = useState<number>(0);
  const [activeScaleIndex, setActiveScaleIndex] = useState<number | null>(null);
  const [hasInitializedScale, setHasInitializedScale] = useState<boolean>(false);

  const { showSnackbar } = useSnackbar();

  const loadProducts = async () => {
    setProducts(null);
    const products = await StripeApi.loadProducts();
    setProducts(products);
  };

  const handleSelect = async (product: SubscriptionProduct) => {
    if (!isUserSignedIn) {
      await showAuthModal();
      // Close modal for free plans after auth (no checkout needed)
      if (product.price === 0) {
        closeSubscriptionsModal();
      }
      return;
    }
    setSelectedProduct(product.id);
    setIsCheckoutSessionLoading(true);
    trackEvent("purchase_initiated");
    try {
      const checkoutSession = await StripeApi.createCheckoutSession(
        product.price_id
      );
      savePromptInLocalStorage();
      window.location.href = checkoutSession.url;
    } catch (e) {
      showSnackbar(
        "Error creating checkout session. Try again later or contact us on: support@darvin.dev",
        {
          type: "error",
          duration: null,
        }
      );
    }
    setIsCheckoutSessionLoading(false);
    setSelectedProduct(null);
  };

  useEffect(() => {
    loadProducts();
  }, [isUserSignedIn]);

  const plans = products ? transformProducts(products) : null;

  // Select the active Scale option in the dropdown when products load (only once)
  useEffect(() => {
    if (plans?.scaleOptions && !hasInitializedScale) {
      const activeIndex = plans.scaleOptions.findIndex(
        (option) => option.status === "active"
      );
      if (activeIndex !== -1) {
        setSelectedScaleIndex(activeIndex);
        setActiveScaleIndex(activeIndex);
      }
      setHasInitializedScale(true);
    }
  }, [plans, hasInitializedScale]);

  return (
    <div
      className={`absolute top-0 left-0 w-full h-full
    pt-4 pb-6 flex flex-col justify-between ${isCompactView ? "overflow-y-auto" : (isUserSignedIn ? "overflow-y-auto" : "overflow-hidden")}
    ${layout.position.relative} ${layout.zIndex.base}`}
    >
      {plans === null ? (
        <div
          className={`${layout.flex.center} flex-col
        h-full w-full ${spacing.gap.xs} px-8 ${colors.text.primary}`}
        >
          <LuLoaderCircle size={24} className="animate-spin" />
          <p>Loading plans...</p>
        </div>
      ) : (
        <>
          <div className={`px-8 flex flex-col ${spacing.gap.sm}`}>
            <h2 className={`${typography.size["3xl"]} ${typography.weight.bold} ${colors.text.primary} text-center pt-4 pb-2`}>
              Pricing
            </h2>
            <div
              className={`${
                isCompactView ? "flex flex-col items-center" : "flex flex-row justify-center"
              } ${spacing.gap.sm}`}
            >
              <SubscriptionsModalItem
                product={plans.firstPlan}
                isDisabled={isCheckoutSessionLoading}
                isLoading={selectedProduct === plans.firstPlan.id}
                onSelect={() => handleSelect(plans.firstPlan)}
              />
              <SubscriptionsModalItem
                product={plans.secondPlan}
                isDisabled={isCheckoutSessionLoading}
                isLoading={selectedProduct === plans.secondPlan.id}
                onSelect={() => handleSelect(plans.secondPlan)}
                isPopular={true}
              />
              <SubscriptionsModalItem
                product={plans.scaleOptions[selectedScaleIndex]}
                isDisabled={isCheckoutSessionLoading}
                isLoading={
                  selectedProduct === plans.scaleOptions[selectedScaleIndex].id
                }
                onSelect={() =>
                  handleSelect(plans.scaleOptions[selectedScaleIndex])
                }
                isScaleCard={true}
                scaleOptions={plans.scaleOptions}
                selectedScaleIndex={selectedScaleIndex}
                onScaleSelectionChange={setSelectedScaleIndex}
                activeScaleIndex={activeScaleIndex}
              />
            </div>
          </div>
          {isUserSignedIn && (
            <div className={`flex flex-col items-center ${spacing.gap.xs} px-8 mt-4`}>
              <SubscriptionsRemainingTokens />
              <RedirectToUserPanel />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RedirectToUserPanel() {
  const { navigateToUserPanel } = useSubscriptionActions();
  const [isRedirecting, setIsRedirecting] = useState(false);

  const onClick = async () => {
    setIsRedirecting(true);
    await navigateToUserPanel();
    setIsRedirecting(false);
  };

  return (
    <button
      id="subscriptions-modal-redirect-to-user-panel"
      onClick={onClick}
      className={`text-[min(14px,3.2vw)] bg-transparent
        cursor-pointer ${layout.flex.center} gap-1
        ${effects.transition.default}
        ${components.button.footer}
        ${effects.borderRadius.default}`}
      style={{ color: colors.primary.blueAlt }}
      disabled={isRedirecting}
    >
      Manage your payment preferences and check past payments
      {isRedirecting && <LuLoaderCircle className="animate-spin ml-1" />}
    </button>
  );
}
